import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Heart } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../ui/navigation-menu';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1cfa97e1-e5a1-4141-a583-88431abcdf42/artifacts/4wtpibus_opad_logo-01.jpg";
const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

const homeLinks = [
  { title: 'Overview', href: '/overview', description: 'Welcome to OPAD - Hope in Action' },
  { title: 'Global Impact Highlights', href: '/impact', description: 'See our worldwide achievements' },
  { title: 'Latest News & Events', href: '/news-events', description: 'Stay updated with OPAD news' },
  { title: 'Call to Action', href: '/take-action', description: 'Donate or Get Involved' },
  { title: 'Emergency Appeal', href: '/emergency-appeal', description: 'Gamo Zone Landslide Response' },
];

const whereWeWorkLinks = [
  { title: 'Global Presence', href: '/where-we-work', description: 'Our six regional offices worldwide' },
  { title: 'OPAD West Africa', href: '/opad-west-africa', description: 'Nigeria, Ghana, Senegal & more' },
  { title: 'OPAD East Africa', href: '/opad-east-africa', description: 'Ethiopia, Kenya, Uganda & more' },
  { title: 'OPAD Southern Africa', href: '/opad-southern-africa', description: 'South Africa, Zimbabwe & more' },
  { title: 'OPAD Europe', href: '/opad-europe', description: 'Stockholm HQ & European offices' },
  { title: 'OPAD USA / North America', href: '/opad-usa', description: 'Los Angeles & North America' },
  { title: 'OPAD Asia', href: '/opad-asia', description: 'Beijing & Asia Pacific offices' },
];

const aboutLinks = [
  { title: 'Who We Are', href: '/about', description: 'Learn about our mission and values' },
  { title: 'Our Team', href: '/team', description: 'Meet our dedicated volunteers' },
  { title: 'OPAD Consortium', href: '/consortium', description: 'Youth Skills Development Initiative' },
  { title: 'History & Background', href: '/about#history', description: 'Our journey since inception' },
  { title: 'Governance & Leadership', href: '/about#leadership', description: 'Our strategic leadership' },
  { title: 'Partners & Alliances', href: '/about#partners', description: 'Our strategic partners' },
];

const programLinks = [
  { title: 'Agriculture & Rural Development', href: '/programs/agriculture', description: 'Sustainable farming & food security' },
  { title: 'Climate Action & Environment', href: '/programs/climate-action', description: 'Environmental sustainability' },
  { title: 'Education & Lifelong Learning', href: '/programs/education', description: 'Building future generations' },
  { title: 'Health & Well-being', href: '/programs/health', description: 'Community health programs' },
  { title: 'International Cooperation', href: '/programs/international-cooperation', description: 'Global development partnerships' },
  { title: 'View All Programs', href: '/programs', description: 'Explore our full portfolio' },
];

const involveLinks = [
  { title: 'Volunteer Globally', href: '/get-involved#volunteer', description: 'Join our volunteer network' },
  { title: 'Internship Opportunities', href: '/internship', description: 'Remote & In-Person programmes' },
  { title: 'Careers', href: '/get-involved#careers', description: 'Work with OPAD' },
  { title: 'Partnership with OPAD', href: '/partnership', description: 'Associate & Institutional partnerships' },
];

const mediaLinks = [
  { title: 'Newsletter & Field Reports', href: '/newsletter-reports', description: 'OPAD IN ACTION reports from the field' },
  { title: 'News & Press Releases', href: '/media#news', description: 'Latest updates from OPAD' },
  { title: 'Publications & Reports', href: '/media#publications', description: 'Annual reports & documents' },
  { title: 'Research & Policy Briefs', href: '/media#research', description: 'Studies and analysis' },
  { title: 'Photo Gallery', href: '/media#photos', description: 'Images from our programmes' },
  { title: 'Video Gallery', href: '/media#videos', description: 'Documentary & interviews' },
  { title: 'Events & Meetings', href: '/events', description: 'Conferences, UNHCR Sessions & more' },
  { title: 'Policy Engagement', href: '/policy-engagement', description: 'UN/EU statements & advocacy' },
];

const contactLinks = [
  { title: 'Contact Us', href: '/contact', description: 'Get in touch with OPAD' },
  { title: 'Global Offices Directory', href: '/contact#offices', description: 'Find our regional offices' },
  { title: 'Media & Partnerships', href: '/contact#partnerships', description: 'Press & collaboration inquiries' },
  { title: 'Request Information', href: '/contact#request', description: 'General inquiries' },
];

const conferenceLinks = [
  { title: 'All Upcoming Conferences', href: '/conferences', description: 'Full conference calendar' },
  { title: 'HLPF 2026 — UN Headquarters', href: '/conferences#hlpf-2026', description: 'July 2026 · New York · SDG Review' },
  { title: 'COP31 — UN Climate Change', href: '/conferences#cop31', description: 'November 2026 · Climate Negotiations' },
  { title: 'World Humanitarian Summit Follow-up', href: '/conferences#whs-followup', description: 'September 2026 · Geneva' },
  { title: '77th UNHCR Executive Committee', href: '/conferences#unhcr-77', description: 'October 2026 · Geneva' },
  { title: 'EU CERV — Buarcos, Portugal', href: '/conferences#cerv-buarcos', description: '25–28 February 2026' },
  { title: 'EU CERV — Santiago, Spain', href: '/conferences#cerv-santiago', description: '1–4 March 2026' },
  { title: 'OPAD Regional Conference — Addis Ababa', href: '/conferences#east-africa-conf', description: 'Q4 2026 · Inclusive Development' },
];

const ListItem = React.forwardRef(({ className, title, children, external, ...props }, ref) => {
  if (external) {
    return (
      <li>
        <NavigationMenuLink asChild>
          <a
            ref={ref}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-[#7BC4E2] focus:bg-slate-100",
              className
            )}
            {...props}
          >
            <div className="text-sm font-medium leading-none text-[#E08B7A]">{title}</div>
            <p className="line-clamp-2 text-sm leading-snug text-slate-500">
              {children}
            </p>
          </a>
        </NavigationMenuLink>
      </li>
    );
  }
  
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 hover:text-[#7BC4E2] focus:bg-slate-100",
            className
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-slate-500">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled ? "bg-white/95 backdrop-blur-md shadow-md" : "bg-transparent"
      )}
      data-testid="navbar"
    >
      <nav className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <img
              src={LOGO_URL}
              alt="OPAD Logo"
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={cn(
                      "text-sm font-medium",
                      location.pathname === '/' ? "text-[#7BC4E2]" : scrolled ? "text-slate-700" : "text-slate-700"
                    )}
                    data-testid="nav-home-trigger"
                  >
                    Home
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                      {homeLinks.map((link) => (
                        <ListItem 
                          key={link.title} 
                          title={link.title} 
                          to={link.external ? undefined : link.href}
                          href={link.external ? link.href : undefined}
                          external={link.external}
                        >
                          {link.description}
                        </ListItem>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={cn(
                      "text-sm font-medium",
                      scrolled ? "text-slate-700" : "text-slate-700"
                    )}
                    data-testid="nav-about-trigger"
                  >
                    About Us
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                      {aboutLinks.map((link) => (
                        <ListItem key={link.title} title={link.title} to={link.href}>
                          {link.description}
                        </ListItem>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={cn(
                      "text-sm font-medium",
                      location.pathname === '/where-we-work' ? "text-[#7BC4E2]" : scrolled ? "text-slate-700" : "text-slate-700"
                    )}
                    data-testid="nav-where-we-work-trigger"
                  >
                    Where We Work
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                      {whereWeWorkLinks.map((link) => (
                        <ListItem key={link.title} title={link.title} to={link.href}>
                          {link.description}
                        </ListItem>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={cn(
                      "text-sm font-medium",
                      scrolled ? "text-slate-700" : "text-slate-700"
                    )}
                    data-testid="nav-programs-trigger"
                  >
                    Our Work
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                      {programLinks.map((link) => (
                        <ListItem key={link.title} title={link.title} to={link.href}>
                          {link.description}
                        </ListItem>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={cn(
                      "text-sm font-medium",
                      scrolled ? "text-slate-700" : "text-slate-700"
                    )}
                    data-testid="nav-involve-trigger"
                  >
                    Get Involved
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                      {involveLinks.map((link) => (
                        <ListItem key={link.title} title={link.title} to={link.href}>
                          {link.description}
                        </ListItem>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={cn(
                      "text-sm font-medium",
                      location.pathname === '/media' || location.pathname === '/policy-engagement' ? "text-[#7BC4E2]" : scrolled ? "text-slate-700" : "text-slate-700"
                    )}
                    data-testid="nav-media-trigger"
                  >
                    Media
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                      {mediaLinks.map((link) => (
                        <ListItem key={link.title} title={link.title} to={link.href}>
                          {link.description}
                        </ListItem>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={cn(
                      "text-sm font-medium",
                      location.pathname === '/conferences' ? "text-[#7BC4E2]" : scrolled ? "text-slate-700" : "text-slate-700"
                    )}
                    data-testid="nav-conferences-trigger"
                  >
                    Conferences
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[560px] md:grid-cols-2">
                      {conferenceLinks.map((link) => (
                        <ListItem key={link.title} title={link.title} to={link.href}>
                          {link.description}
                        </ListItem>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger 
                    className={cn(
                      "text-sm font-medium",
                      location.pathname === '/contact' ? "text-[#7BC4E2]" : scrolled ? "text-slate-700" : "text-slate-700"
                    )}
                    data-testid="nav-contact-trigger"
                  >
                    Contact
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                      {contactLinks.map((link) => (
                        <ListItem key={link.title} title={link.title} to={link.href}>
                          {link.description}
                        </ListItem>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <a
              href={PAYPAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-4"
              data-testid="donate-button-nav"
            >
              <Button className="bg-[#E08B7A] text-white hover:bg-[#D07A69] rounded-full px-6 font-semibold flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Donate
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-md text-slate-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-slate-100 pb-4" data-testid="mobile-menu">
            <div className="space-y-1 pt-4">
              <Link to="/" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Home</Link>
              <Link to="/about" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">About Us</Link>
              <Link to="/team" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Our Team</Link>
              <Link to="/where-we-work" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Where We Work</Link>
              <Link to="/programs" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Our Work</Link>
              <Link to="/get-involved" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Get Involved</Link>
              <Link to="/internship" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Internship</Link>
              <Link to="/partnership" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Partnership</Link>
              <Link to="/media" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Media</Link>
              <Link to="/newsletter-reports" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Newsletter & Field Reports</Link>
              <Link to="/policy-engagement" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Policy Engagement</Link>
              <Link to="/conferences" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Conferences</Link>
              <Link to="/transparency" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Transparency</Link>
              <Link to="/contact" className="block px-4 py-2 text-slate-700 hover:bg-slate-50">Contact</Link>
              <div className="px-4 pt-4">
                <a
                  href={PAYPAL_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  data-testid="donate-button-mobile"
                >
                  <Button className="w-full bg-[#E08B7A] text-white hover:bg-[#D07A69] rounded-full font-semibold flex items-center justify-center gap-2">
                    <Heart className="w-4 h-4" />
                    Donate Now
                  </Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
